import {
  createContext, useContext, useState, type ReactNode, useCallback,
} from "react";
import { RecommendationFormModal } from "@/features/sponsorship/components/RecommendationFormModal";
import { useCurrentCampaign } from "@/features/sponsorship/hooks/useCurrentCampaign";

type RecommendationModalContextType = {
  openRecommendationModal: () => void
  canRecommend: boolean
  remainingRecommendations: number
};

const RecommendationModalContext = createContext<RecommendationModalContextType | undefined>(undefined);

type RecommendationModalProviderProps = { children: ReactNode };

// Monté au niveau du SponsorshipLayout : la modale de recommandation devient
// déclenchable depuis n'importe quel onglet (bannière comprise) et n'est rendue
// qu'une seule fois. `canRecommend` reste false pendant le chargement de la
// campagne → le CTA de bannière est masqué sans flash.
export const RecommendationModalProvider = ({ children }: RecommendationModalProviderProps) => {
  const { hasCampaign, remainingRecommendations } = useCurrentCampaign();
  const [isOpen, setIsOpen] = useState(false);

  const openRecommendationModal = useCallback(() => setIsOpen(true), []);
  const closeRecommendationModal = useCallback(() => setIsOpen(false), []);

  const canRecommend = hasCampaign && remainingRecommendations > 0;

  const value: RecommendationModalContextType = {
    openRecommendationModal,
    canRecommend,
    remainingRecommendations,
  };

  return (
    <RecommendationModalContext.Provider value={value}>
      {children}
      <RecommendationFormModal
        open={isOpen}
        onClose={closeRecommendationModal}
        remainingRecommendations={remainingRecommendations}
      />
    </RecommendationModalContext.Provider>
  );
};

// Volontairement non-throwing (contrairement à useVoucherDrawer) : la page
// Aperçu est testée isolément, sans le layout donc sans provider. Le fallback
// neutralise simplement le CTA (masqué / no-op) au lieu de faire planter.
export const useRecommendationModal = (): RecommendationModalContextType => {
  const context = useContext(RecommendationModalContext);
  if (!context) {
    return { openRecommendationModal: () => {}, canRecommend: false, remainingRecommendations: 0 };
  }
  return context;
};
